// Phase 49 — 9-role permission catalog (Stage 1: foundation, no enforcement).
//
// SOURCE OF TRUTH for "what can this role do." Mirrors the DB exactly:
//   - team_members.role CHECK: the 9 canonical roles + transitional 'agent'.
//   - public.role_capability_defaults: ROLE_CAPABILITY_DEFAULTS below.
//   - public.capability_level() / has_capability(): capabilityLevel() / can().
//
// Resolver semantics (identical to the SQL capability_level()):
//   owner            -> 'full' for everything (short-circuit)
//   per-member override present -> 'full' if grant else 'none'
//   else role default ('agent' treated as 'sales_rep') -> level, else 'none'
//
// can(role, capability, {min}) mirrors has_capability(): a 'full' level
// satisfies any min; 'own' satisfies 'own'/'view'; 'view' satisfies 'view'.
//
// Stage 1 changes NO enforcement. RLS and screens still use the legacy
// owner/admin/agent gates; the legacy can.* predicates below are preserved as
// thin wrappers (now backed by the capability map) so behavior is unchanged for
// existing roles. Enforcement migrates in later stages.
//
// If you change ROLE_CAPABILITY_DEFAULTS, regenerate the seed migration:
//   npm run gen:role-seed

import type { Profile } from '../types/database'

// ---- roles ----

export type CanonicalRole =
  | 'owner'
  | 'co_owner'
  | 'manager'
  | 'admin'
  | 'sales_rep'
  | 'crew_lead'
  | 'technician'
  | 'customer_service'
  | 'bookkeeper'

// 'agent' is the deprecated legacy role; it maps to 'sales_rep' everywhere.
export type TeamRole = CanonicalRole | 'agent'

export const CANONICAL_ROLES: CanonicalRole[] = [
  'owner',
  'co_owner',
  'manager',
  'admin',
  'sales_rep',
  'crew_lead',
  'technician',
  'customer_service',
  'bookkeeper',
]

export function normalizeRole(role: TeamRole): CanonicalRole {
  return role === 'agent' ? 'sales_rep' : role
}

// Roles an owner can INVITE a new member as. Never owner, never co_owner.
// Order: management first, then field / specialist.
export const INVITABLE_ROLES: CanonicalRole[] = [
  'manager',
  'admin',
  'sales_rep',
  'crew_lead',
  'technician',
  'customer_service',
  'bookkeeper',
]

// Roles an owner can ASSIGN to an existing member (the invitable set plus
// co_owner, which an owner can promote into). Never owner (ownership transfer
// is a separate destructive action, out of scope here).
export const ASSIGNABLE_ROLES: CanonicalRole[] = ['co_owner', ...INVITABLE_ROLES]

// ---- capabilities ----

export type CapabilityLevel = 'full' | 'own' | 'view' | 'none'

export interface CapabilityDef {
  key: string
  area: string
  description: string
}

// The 80 gateable capabilities, grouped by area. Keys match the DB exactly.
export const CAPABILITY_CATALOG: CapabilityDef[] = [
  { key: 'customers.view', area: 'Customers', description: 'View customers' },
  { key: 'customers.create', area: 'Customers', description: 'Create a customer' },
  { key: 'customers.edit', area: 'Customers', description: 'Edit a customer' },
  { key: 'customers.delete', area: 'Customers', description: 'Delete a customer' },
  { key: 'customers.import', area: 'Customers', description: 'Bulk import customers' },
  { key: 'customers.export', area: 'Customers', description: 'Export customer data' },
  { key: 'leads.view', area: 'Leads', description: 'View leads' },
  { key: 'leads.edit', area: 'Leads', description: 'Edit / assign leads' },
  { key: 'leads.delete', area: 'Leads', description: 'Delete a lead' },
  { key: 'leads.ingestion_config', area: 'Leads', description: 'Configure lead ingestion sources' },
  { key: 'opportunities.view', area: 'Sales', description: 'View opportunities' },
  { key: 'opportunities.create', area: 'Sales', description: 'Create an opportunity' },
  { key: 'opportunities.edit', area: 'Sales', description: 'Edit / move an opportunity' },
  { key: 'opportunities.convert', area: 'Sales', description: 'Convert a won opportunity' },
  { key: 'pipelines.manage', area: 'Sales', description: 'Manage pipelines + stages' },
  { key: 'jobs.view', area: 'Jobs', description: 'View jobs' },
  { key: 'jobs.create', area: 'Jobs', description: 'Create a job' },
  { key: 'jobs.edit', area: 'Jobs', description: 'Edit a job' },
  { key: 'jobs.complete', area: 'Jobs', description: 'Mark a job complete' },
  { key: 'dispatch.manage', area: 'Jobs', description: 'Build + publish the dispatch board' },
  { key: 'schedule.view_own', area: 'Jobs', description: 'View own schedule' },
  { key: 'calendar.manage', area: 'Jobs', description: 'Manage the team calendar + booking' },
  { key: 'tasks.manage', area: 'Jobs', description: 'Create / assign / complete tasks' },
  // RECORD-TYPES-1 — a business can invent its own record types, and filling
  // one in is daily work, not a settings change. There was no capability that
  // meant "may create and edit a record of a type this business invented", so
  // rather than misuse tasks.manage (a custom record is not a task) or gate
  // daily work behind settings.account_manage (which would make custom types
  // unusable by the people who use them), this is its own key. DEFINING a type
  // still needs settings.account_manage; only filling one in needs this.
  {
    key: 'records.manage',
    area: 'Customers',
    description: 'Create / edit records of your own record types',
  },
  { key: 'service_plans.manage', area: 'Jobs', description: 'Manage recurring service plans' },
  { key: 'canvass.use', area: 'Jobs', description: 'Use the canvassing map' },
  { key: 'quotes.view', area: 'Quotes', description: 'View quotes' },
  { key: 'quotes.create', area: 'Quotes', description: 'Create a quote' },
  { key: 'quotes.edit', area: 'Quotes', description: 'Edit a quote' },
  { key: 'quotes.send', area: 'Quotes', description: 'Send a quote' },
  { key: 'quotes.delete', area: 'Quotes', description: 'Delete a quote' },
  { key: 'invoices.view', area: 'Invoices', description: 'View invoices' },
  { key: 'invoices.create', area: 'Invoices', description: 'Create an invoice' },
  { key: 'invoices.edit', area: 'Invoices', description: 'Edit an invoice' },
  { key: 'invoices.send', area: 'Invoices', description: 'Send an invoice' },
  { key: 'invoices.void', area: 'Invoices', description: 'Void an invoice' },
  { key: 'money.section.view', area: 'Money', description: 'See the Money section' },
  { key: 'billing.view', area: 'Money', description: 'View billing / usage' },
  { key: 'billing.manage', area: 'Money', description: 'Manage plan + top ups' },
  { key: 'payment_account.view', area: 'Money', description: 'View the payment account' },
  { key: 'payment_account.manage', area: 'Money', description: 'Manage KYC / payout account' },
  { key: 'payouts.cashout', area: 'Money', description: 'Initiate a cash out' },
  { key: 'payroll.view_dashboard', area: 'Money', description: 'View the payroll dashboard' },
  { key: 'payroll.manage_periods', area: 'Money', description: 'Open / close / pay pay periods' },
  { key: 'paychecks.view_own', area: 'Money', description: 'View own paychecks' },
  { key: 'compensation.view_own', area: 'Money', description: 'View own compensation profile' },
  { key: 'compensation.view_any', area: 'Money', description: 'View any compensation profile' },
  { key: 'compensation.edit', area: 'Money', description: 'Edit a compensation profile' },
  { key: 'commissions.rules_manage', area: 'Commissions', description: 'Manage commission rules' },
  { key: 'commissions.view_all', area: 'Commissions', description: 'View all commissions' },
  { key: 'commissions.view_own', area: 'Commissions', description: 'View own commissions' },
  { key: 'marketing.page_edit', area: 'Marketing', description: 'Edit / publish the public page' },
  { key: 'marketing.page_analytics', area: 'Marketing', description: 'View page analytics' },
  { key: 'marketing.campaigns_manage', area: 'Marketing', description: 'Manage email campaigns' },
  { key: 'niche_template.apply', area: 'Marketing', description: 'Apply a niche template' },
  { key: 'automations.view', area: 'Agents', description: 'View agents' },
  { key: 'automations.manage', area: 'Agents', description: 'Manage agents + templates' },
  { key: 'automations.runs_view', area: 'Agents', description: 'View agent runs' },
  { key: 'inbox.view', area: 'Messages', description: 'View the inbox' },
  { key: 'inbox.send', area: 'Messages', description: 'Send messages' },
  { key: 'inbox.channels_manage', area: 'Messages', description: 'Connect / configure channels' },
  { key: 'phone.setup_manage', area: 'Phone', description: 'Provision number + voice settings' },
  { key: 'phone.call', area: 'Phone', description: 'Place outbound calls' },
  { key: 'phone.calls_view', area: 'Phone', description: 'View call history + recordings' },
  { key: 'reviews.triage', area: 'Reviews', description: 'Triage / respond to feedback' },
  { key: 'reviews.settings_manage', area: 'Reviews', description: 'Manage reputation settings' },
  { key: 'reports.team_view', area: 'Reports', description: 'View team reports / revenue' },
  { key: 'leaderboard.view', area: 'Reports', description: 'View the sales leaderboard' },
  { key: 'ai.tools_use', area: 'AI', description: 'Use AI tools' },
  { key: 'team.view', area: 'Team', description: 'View the team roster' },
  { key: 'team.invite', area: 'Team', description: 'Invite team members' },
  { key: 'team.change_role', area: 'Team', description: "Change a member's role" },
  { key: 'team.remove', area: 'Team', description: 'Remove a team member' },
  {
    key: 'team.overrides_manage',
    area: 'Team',
    description: 'Grant / deny per-member capabilities',
  },
  { key: 'crews.manage', area: 'Team', description: 'Manage crews + crew membership' },
  { key: 'crews.view', area: 'Team', description: 'View crews' },
  {
    key: 'settings.account_manage',
    area: 'Settings',
    description: 'Manage business / account settings',
  },
  { key: 'settings.integrations_manage', area: 'Settings', description: 'Manage integrations' },
  {
    key: 'account.delete_transfer',
    area: 'Settings',
    description: 'Delete or transfer the company',
  },
  { key: 'time.clock_self', area: 'Time', description: 'Clock in / out (own)' },
  { key: 'time.entries_view_all', area: 'Time', description: "View all members' time entries" },
  { key: 'time.entries_edit', area: 'Time', description: 'Edit completed time entries' },
]

export type CapabilityKey = (typeof CAPABILITY_CATALOG)[number]['key']

export const CAPABILITY_KEYS: string[] = CAPABILITY_CATALOG.map((c) => c.key)

// ---- role -> capability default matrix (mirrors role_capability_defaults) ----
// Only non-'none' entries are listed; a missing entry resolves to 'none'.
// owner + co_owner are generated below so they can never drift from the catalog.

type RoleMatrix = Record<CanonicalRole, Partial<Record<string, CapabilityLevel>>>

const OWNER_FULL: Record<string, CapabilityLevel> = Object.fromEntries(
  CAPABILITY_KEYS.map((k) => [k, 'full']),
)
// Co-owner = owner minus the ability to delete / transfer the company.
const CO_OWNER: Record<string, CapabilityLevel> = (() => {
  const m = { ...OWNER_FULL }
  delete m['account.delete_transfer']
  return m
})()

export const ROLE_CAPABILITY_DEFAULTS: RoleMatrix = {
  owner: OWNER_FULL,
  co_owner: CO_OWNER,
  manager: {
    'customers.view': 'full',
    'customers.create': 'full',
    'customers.edit': 'full',
    'customers.delete': 'full',
    'customers.import': 'full',
    'customers.export': 'full',
    'leads.view': 'full',
    'leads.edit': 'full',
    'leads.delete': 'full',
    'leads.ingestion_config': 'full',
    'opportunities.view': 'full',
    'opportunities.create': 'full',
    'opportunities.edit': 'full',
    'opportunities.convert': 'full',
    'pipelines.manage': 'full',
    'jobs.view': 'full',
    'jobs.create': 'full',
    'jobs.edit': 'full',
    'jobs.complete': 'full',
    'dispatch.manage': 'full',
    'schedule.view_own': 'full',
    'calendar.manage': 'full',
    'tasks.manage': 'full',
    'records.manage': 'full',
    'service_plans.manage': 'full',
    'canvass.use': 'full',
    'quotes.view': 'full',
    'quotes.create': 'full',
    'quotes.edit': 'full',
    'quotes.send': 'full',
    'quotes.delete': 'full',
    'invoices.view': 'full',
    'invoices.create': 'full',
    'invoices.edit': 'full',
    'invoices.send': 'full',
    'invoices.void': 'full',
    'money.section.view': 'full',
    'billing.view': 'full',
    'billing.manage': 'full',
    'payment_account.view': 'full',
    'payroll.view_dashboard': 'full',
    'payroll.manage_periods': 'full',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'compensation.view_any': 'full',
    'commissions.rules_manage': 'full',
    'commissions.view_all': 'full',
    'commissions.view_own': 'full',
    'marketing.page_edit': 'full',
    'marketing.page_analytics': 'full',
    'marketing.campaigns_manage': 'full',
    'niche_template.apply': 'full',
    'automations.view': 'full',
    'automations.manage': 'full',
    'automations.runs_view': 'full',
    'inbox.view': 'full',
    'inbox.send': 'full',
    'inbox.channels_manage': 'full',
    'phone.setup_manage': 'full',
    'phone.call': 'full',
    'phone.calls_view': 'full',
    'reviews.triage': 'full',
    'reviews.settings_manage': 'full',
    'reports.team_view': 'full',
    'leaderboard.view': 'full',
    'ai.tools_use': 'full',
    'team.view': 'full',
    'team.invite': 'full',
    'team.change_role': 'full',
    'team.remove': 'full',
    'settings.account_manage': 'full',
    'settings.integrations_manage': 'full',
    'time.clock_self': 'full',
    'time.entries_view_all': 'full',
    'time.entries_edit': 'full',
    'crews.manage': 'full',
    'crews.view': 'full',
  },
  admin: {
    'customers.view': 'full',
    'customers.create': 'full',
    'customers.edit': 'full',
    'customers.delete': 'full',
    'customers.import': 'full',
    'customers.export': 'full',
    'leads.view': 'full',
    'leads.edit': 'full',
    'leads.delete': 'full',
    'leads.ingestion_config': 'full',
    'opportunities.view': 'full',
    'opportunities.create': 'full',
    'opportunities.edit': 'full',
    'opportunities.convert': 'full',
    'pipelines.manage': 'full',
    'jobs.view': 'full',
    'jobs.create': 'full',
    'jobs.edit': 'full',
    'jobs.complete': 'full',
    'dispatch.manage': 'full',
    'schedule.view_own': 'full',
    'calendar.manage': 'full',
    'tasks.manage': 'full',
    'records.manage': 'full',
    'service_plans.manage': 'full',
    'canvass.use': 'full',
    'quotes.view': 'full',
    'quotes.create': 'full',
    'quotes.edit': 'full',
    'quotes.send': 'full',
    'quotes.delete': 'full',
    'invoices.view': 'full',
    'invoices.create': 'full',
    'invoices.edit': 'full',
    'invoices.send': 'full',
    'invoices.void': 'full',
    'money.section.view': 'full',
    'billing.view': 'full',
    'payment_account.view': 'full',
    'payroll.view_dashboard': 'full',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'compensation.view_any': 'full',
    'commissions.rules_manage': 'full',
    'commissions.view_all': 'full',
    'commissions.view_own': 'full',
    'marketing.page_edit': 'full',
    'marketing.page_analytics': 'full',
    'marketing.campaigns_manage': 'full',
    'niche_template.apply': 'full',
    'automations.view': 'full',
    'automations.manage': 'full',
    'automations.runs_view': 'full',
    'inbox.view': 'full',
    'inbox.send': 'full',
    'inbox.channels_manage': 'full',
    'phone.setup_manage': 'full',
    'phone.call': 'full',
    'phone.calls_view': 'full',
    'reviews.triage': 'full',
    'reviews.settings_manage': 'full',
    'reports.team_view': 'full',
    'leaderboard.view': 'full',
    'ai.tools_use': 'full',
    'team.view': 'full',
    'settings.account_manage': 'full',
    'settings.integrations_manage': 'full',
    'time.clock_self': 'full',
    'time.entries_view_all': 'full',
    'time.entries_edit': 'full',
    'crews.manage': 'full',
    'crews.view': 'full',
  },
  sales_rep: {
    'customers.view': 'own',
    'customers.create': 'full',
    'customers.edit': 'own',
    'leads.view': 'own',
    'leads.edit': 'own',
    'opportunities.view': 'own',
    'opportunities.create': 'full',
    'opportunities.edit': 'own',
    'opportunities.convert': 'own',
    'jobs.view': 'own',
    'jobs.create': 'full',
    'jobs.edit': 'own',
    'jobs.complete': 'own',
    'schedule.view_own': 'full',
    'tasks.manage': 'own',
    'records.manage': 'own',
    'canvass.use': 'full',
    'quotes.view': 'own',
    'quotes.create': 'full',
    'quotes.edit': 'own',
    'quotes.send': 'own',
    'invoices.view': 'own',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'commissions.view_own': 'full',
    'inbox.view': 'own',
    'inbox.send': 'own',
    'phone.call': 'full',
    'phone.calls_view': 'own',
    'leaderboard.view': 'full',
    'team.view': 'view',
    'time.clock_self': 'full',
  },
  crew_lead: {
    'customers.view': 'full',
    'leads.view': 'view',
    'opportunities.view': 'view',
    'opportunities.create': 'full',
    'opportunities.edit': 'own',
    'jobs.view': 'full',
    'jobs.create': 'full',
    'jobs.edit': 'own',
    'jobs.complete': 'own',
    'dispatch.manage': 'full',
    'schedule.view_own': 'full',
    'calendar.manage': 'own',
    'tasks.manage': 'own',
    'records.manage': 'own',
    'canvass.use': 'full',
    'quotes.view': 'view',
    'quotes.create': 'full',
    'quotes.edit': 'own',
    'quotes.send': 'own',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'commissions.view_own': 'full',
    'phone.call': 'full',
    'leaderboard.view': 'full',
    'team.view': 'view',
    'time.clock_self': 'full',
    'time.entries_view_all': 'full',
    'crews.view': 'view',
  },
  // ROLE-GATE-2 FIX 3: TECHNICIANS SELL ON SITE.
  //
  // The shipped set held 12 of 81 capabilities and covered none of the work a
  // real exterior-cleaning tech does at a customer's house: they write the
  // quote, raise the invoice and take the payment while standing there. The
  // five money keys below were added for exactly that. Levels are 'own'
  // wherever the catalog supports it, so a tech sees the documents attached
  // to their own jobs rather than the whole company's book.
  //
  // NOT granted, deliberately: invoices.edit, invoices.void, quotes.delete,
  // money.section.view, payouts.cashout, payroll.*, billing.*. A tech can
  // raise and send money documents; they cannot rewrite, cancel or withdraw
  // money after the fact.
  technician: {
    'customers.view': 'own',
    'quotes.view': 'own',
    'quotes.create': 'own',
    'invoices.view': 'own',
    'invoices.create': 'own',
    'invoices.send': 'own',
    'jobs.view': 'own',
    'jobs.edit': 'own',
    'jobs.complete': 'own',
    'schedule.view_own': 'full',
    'tasks.manage': 'own',
    'records.manage': 'own',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'commissions.view_own': 'full',
    'leaderboard.view': 'full',
    'team.view': 'view',
    'time.clock_self': 'full',
  },
  customer_service: {
    'customers.view': 'full',
    'customers.create': 'full',
    'customers.edit': 'full',
    'customers.import': 'full',
    'leads.view': 'full',
    'leads.edit': 'full',
    'opportunities.view': 'view',
    'opportunities.create': 'full',
    'opportunities.edit': 'own',
    'opportunities.convert': 'own',
    'jobs.view': 'view',
    'jobs.create': 'full',
    'schedule.view_own': 'full',
    'calendar.manage': 'full',
    'tasks.manage': 'full',
    'records.manage': 'full',
    'quotes.view': 'view',
    'quotes.create': 'full',
    'quotes.edit': 'own',
    'quotes.send': 'own',
    'invoices.view': 'view',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'commissions.view_own': 'full',
    'marketing.page_analytics': 'view',
    'marketing.campaigns_manage': 'full',
    'automations.view': 'view',
    'automations.runs_view': 'view',
    'inbox.view': 'full',
    'inbox.send': 'full',
    'inbox.channels_manage': 'full',
    'phone.setup_manage': 'full',
    'phone.call': 'full',
    'phone.calls_view': 'full',
    'reviews.triage': 'full',
    'leaderboard.view': 'full',
    'team.view': 'view',
    'time.clock_self': 'full',
  },
  bookkeeper: {
    'customers.view': 'view',
    'customers.export': 'full',
    'opportunities.view': 'view',
    'jobs.view': 'view',
    'quotes.view': 'view',
    'invoices.view': 'full',
    'invoices.create': 'full',
    'invoices.edit': 'full',
    'invoices.send': 'full',
    'invoices.void': 'full',
    'money.section.view': 'full',
    'billing.view': 'full',
    'payment_account.view': 'view',
    'payroll.view_dashboard': 'full',
    'payroll.manage_periods': 'full',
    'paychecks.view_own': 'full',
    'compensation.view_own': 'full',
    'compensation.view_any': 'view',
    'commissions.rules_manage': 'full',
    'commissions.view_all': 'full',
    'commissions.view_own': 'full',
    'marketing.page_analytics': 'view',
    'reports.team_view': 'full',
    'leaderboard.view': 'full',
    'team.view': 'view',
    'time.clock_self': 'full',
    'time.entries_view_all': 'full',
    'time.entries_edit': 'full',
  },
}

// ---- resolver (mirror of SQL capability_level / has_capability) ----

export type CapabilityOverrides = Record<string, boolean>

export function capabilityLevel(
  role: TeamRole | null | undefined,
  capability: string,
  overrides?: CapabilityOverrides | null,
): CapabilityLevel {
  if (!role) return 'none'
  const r = normalizeRole(role)
  if (r === 'owner') return 'full'
  // ROLE-GATE-2 FIX 2: A REMOVAL MAY NEVER STRIP AN OWNER OR A CO-OWNER.
  //
  // team_member_capabilities.grant_capability is a boolean, so the table
  // expresses removals as well as grants. An owner was already immune above.
  // A co-owner was NOT: a single row with grant_capability=false would have
  // locked one out of their own business, which is the same failure mode as
  // gating a route on account.delete_transfer and is refused for the same
  // reason. co_owner is OWNER_FULL minus that one key, so returning it here
  // is exactly their default; the only thing skipped is the removal.
  if (r === 'co_owner') {
    return capability === 'account.delete_transfer' ? 'none' : 'full'
  }
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, capability)) {
    return overrides[capability] ? 'full' : 'none'
  }
  return ROLE_CAPABILITY_DEFAULTS[r][capability] ?? 'none'
}

function satisfies(level: CapabilityLevel, min: 'full' | 'own' | 'view'): boolean {
  if (level === 'full') return true
  if (level === 'own') return min === 'own' || min === 'view'
  if (level === 'view') return min === 'view'
  return false
}

function canBase(
  role: TeamRole | null | undefined,
  capability: string,
  opts?: { min?: 'full' | 'own' | 'view'; overrides?: CapabilityOverrides | null },
): boolean {
  const level = capabilityLevel(role, capability, opts?.overrides)
  return satisfies(level, opts?.min ?? 'view')
}

// ---- display helpers ----

export function roleLabel(role: TeamRole): string {
  switch (normalizeRole(role)) {
    case 'owner':
      return 'Owner'
    case 'co_owner':
      return 'Co-Owner'
    case 'manager':
      return 'Manager'
    case 'admin':
      return 'Admin'
    case 'sales_rep':
      return 'Sales Rep'
    case 'crew_lead':
      return 'Crew Lead'
    case 'technician':
      return 'Technician'
    case 'customer_service':
      return 'Customer Service'
    case 'bookkeeper':
      return 'Bookkeeper'
  }
}

export function roleDescription(role: TeamRole): string {
  switch (normalizeRole(role)) {
    case 'owner':
      return 'Full access to everything, including the payment account and deleting the company.'
    case 'co_owner':
      return 'Everything the owner can do except deleting or transferring the company.'
    case 'manager':
      return 'Runs operations, sales, team, and billing. Cannot manage the payout account.'
    case 'admin':
      return 'Full access to customers, quotes, invoices, marketing, and automations, plus team view. No payout account.'
    case 'sales_rep':
      return 'Creates and works their own customers, leads, quotes, and opportunities. Own commissions only.'
    case 'crew_lead':
      return 'Runs jobs, dispatch, and time tracking for their crew. Sees assigned work.'
    case 'technician':
      return 'Sees and completes their own assigned jobs and clocks their own time.'
    case 'customer_service':
      return 'Handles the inbox, scheduling, customers, and reviews. No money or settings.'
    case 'bookkeeper':
      return 'Money, billing, invoices, payroll, and reports. Read-only on operational data.'
  }
}

// ---- legacy predicates (Phase 28C) — preserved as thin wrappers ----
// Same names + signatures as before so existing call sites keep working with
// no behavior change for owner/admin/agent. Now backed by the capability map.

interface PlatformAdminCheck {
  is_admin: boolean
}

// `can` is BOTH the new resolver — can(role, capability, {min}) — AND carries
// the legacy Phase 28C predicate methods (can.inviteTeamMembers(role), etc.) so
// every existing call site keeps working unchanged. Functions are objects, so
// we attach the predicates onto the resolver function.
const legacyPredicates = {
  inviteTeamMembers: (role: TeamRole | null): boolean => canBase(role, 'team.invite'),
  manageTeamMembers: (role: TeamRole | null): boolean => canBase(role, 'team.change_role'),
  removeTeamMembers: (role: TeamRole | null): boolean => canBase(role, 'team.remove'),
  viewTeamPage: (role: TeamRole | null): boolean => canBase(role, 'team.view'),
  managePaymentAccount: (role: TeamRole | null): boolean => canBase(role, 'payment_account.manage'),
  viewPaymentAccount: (role: TeamRole | null): boolean => canBase(role, 'payment_account.view'),
  createCustomers: (role: TeamRole | null): boolean => canBase(role, 'customers.create'),
  editAnyCustomer: (role: TeamRole | null): boolean =>
    canBase(role, 'customers.edit', { min: 'full' }),
  deleteCustomers: (role: TeamRole | null): boolean => canBase(role, 'customers.delete'),
  createQuotes: (role: TeamRole | null): boolean => canBase(role, 'quotes.create'),
  editAnyQuote: (role: TeamRole | null): boolean => canBase(role, 'quotes.edit', { min: 'full' }),
  deleteQuotes: (role: TeamRole | null): boolean => canBase(role, 'quotes.delete'),
  manageServicePlans: (role: TeamRole | null): boolean => canBase(role, 'service_plans.manage'),
  viewTeamBilling: (role: TeamRole | null): boolean => canBase(role, 'reports.team_view'),
  manageCommissions: (role: TeamRole | null): boolean => canBase(role, 'commissions.rules_manage'),
  viewOwnCommissions: (role: TeamRole | null): boolean => canBase(role, 'commissions.view_own'),
  viewMoneySection: (role: TeamRole | null): boolean => canBase(role, 'money.section.view'),
  viewOpportunitiesSection: (role: TeamRole | null): boolean => canBase(role, 'opportunities.view'),
  viewPlatformAdmin: (profile: PlatformAdminCheck | null): boolean => profile?.is_admin === true,
}

export const can: typeof canBase & typeof legacyPredicates = Object.assign(
  canBase,
  legacyPredicates,
)

// Per-row edit gate (Phase 28C). Full-edit roles can edit any team row; field
// roles are limited to rows they created. Unchanged behavior for owner/admin/
// agent; the wider role set is mapped sensibly for the foundation stage.
const FULL_EDIT_ROLES = new Set<CanonicalRole>(['owner', 'co_owner', 'manager', 'admin'])

export function canEditEntity(
  role: TeamRole | null,
  currentTeamMemberId: string | null,
  entityCreatedByTeamMemberId: string | null,
): boolean {
  if (!role) return false
  if (FULL_EDIT_ROLES.has(normalizeRole(role))) return true
  if (!currentTeamMemberId || !entityCreatedByTeamMemberId) return false
  return currentTeamMemberId === entityCreatedByTeamMemberId
}

export type { Profile }
